export type {
  MembershipBenefitVariant,
  MembershipShieldVariant,
  MembershipBenefit,
  MembershipTier,
  MembershipPerk,
  MembershipTierFormData,
} from '@dataTypes/membership.type'

import {
  MembershipBenefit,
  MembershipBenefitVariant,
  MembershipPerk,
  MembershipShieldVariant,
  MembershipTier,
  MembershipTierFormData,
} from '@dataTypes/membership.type'

export const MEMBERSHIP_TIER_COLOR_OPTIONS: {
  value: MembershipShieldVariant
  label: string
}[] = [
  { value: 'basic', label: 'Basic' },
  { value: 'bronze', label: 'Bronze' },
  { value: 'silver', label: 'Silver' },
  { value: 'gold', label: 'Gold' },
]

export const createEmptyMembershipTierForm = (): MembershipTierFormData => ({
  name: '',
  monthlyPrice: '',
  annualPrice: '',
  maxMembers: '',
  tierColor: '',
  description: '',
})

export const createMembershipPerkId = (): string =>
  `perk-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`

export const inferBenefitVariant = (
  label: string,
): MembershipBenefitVariant => {
  const normalized = label.toLowerCase()
  if (normalized.includes('priority')) return 'red'
  if (normalized.includes('x-ray') || normalized.includes('panoramic')) {
    return 'green'
  }
  if (normalized.includes('%') || normalized.includes('discount')) {
    return 'indigo'
  }
  return 'blue'
}

export const perksToBenefits = (perks: MembershipPerk[]): MembershipBenefit[] =>
  perks.map((perk) => ({
    label: perk.label,
    variant: inferBenefitVariant(perk.label),
    amount: perk.amount.trim() || null,
    perkType: perk.type === 'discount' ? 'discount' : 'freebie',
    usageLimit: perk.usageLimit ?? null,
  }))

export const tierToFormData = (
  tier: MembershipTier,
): MembershipTierFormData => ({
  name: tier.name,
  monthlyPrice: String(tier.monthlyPrice),
  annualPrice: String(tier.annualPrice),
  maxMembers: tier.maxMembers != null ? String(tier.maxMembers) : '',
  tierColor: tier.shieldVariant,
  description: tier.description ?? '',
})

export const tierToPerks = (tier: MembershipTier): MembershipPerk[] =>
  tier.benefits.map((benefit, index) => {
    const discount = benefit.label.match(/(\d+)\s*%/)
    const isDiscount = benefit.perkType
      ? benefit.perkType === 'discount'
      : benefit.variant === 'indigo' ||
        benefit.label.toLowerCase().includes('discount') ||
        discount != null
    const savedProcedures = benefit.procedures ?? []
    const visibleCodes = savedProcedures
      .slice(0, 2)
      .map((procedure) => procedure.procedure_code)
    return {
      id: `${tier.id}-perk-${index}`,
      label: benefit.label,
      amount: discount?.[1] ?? benefit.amount ?? (isDiscount ? '' : '100'),
      type: isDiscount ? 'discount' : 'freebie',
      usageLimit:
        benefit.usageLimit !== undefined
          ? benefit.usageLimit
          : isDiscount
            ? null
            : 1,
      procedure: savedProcedures.length
        ? visibleCodes[0]
        : isDiscount
          ? 'All procedures'
          : '',
      procedures: savedProcedures.length
        ? visibleCodes
        : isDiscount
          ? ['All procedures']
          : [],
      ...(savedProcedures.length
        ? {
            procedureOptions: savedProcedures.map((procedure) => ({
              code: procedure.procedure_code,
              description: procedure.description ?? '',
            })),
            extraProcedureCount:
              savedProcedures.length > 2
                ? savedProcedures.length - 2
                : undefined,
          }
        : {}),
    }
  })

export const createMembershipInclusion = (): MembershipPerk => ({
  id: createMembershipPerkId(),
  label: '',
  amount: '',
  type: 'freebie',
  usageLimit: 1,
  procedures: [],
})

export const formatMembershipPrice = (value: number): string =>
  `$${value.toFixed(2)}`
